import { LogLevel } from '@datocms/cma-client-browser';
import ApiClient from '@src/apiClient/ApiClient';
import {
  AIFlowRepository,
  AIModelRepository,
  AIPromptRepository,
  AIVoiceRepository,
  AlbumRepository,
  AssetRepository,
  AudioRepository,
  CaptionRepository,
  CommentRepository,
  CompositionRepository,
  DistributionChannelRepository,
  DubbingRepository,
  LanguageRepository,
  PersonRepository,
  PublishRecordRepository,
  ReleasesRepository,
  SpeakerRepository,
  StoryRepository,
  TemplatedPromptsRepository,
  TranscriptRepository,
  TranslationHandlerRepository,
  TranslationMemoryCollectionRepository,
  TranslationMemoryEntryRepository,
  TranslationMemoryImportRepository,
  TranslationRepository,
  VideoRepository,
} from '@src/repositories';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { CachedVideoRepository } from '@src/repositories';
import { VideoTemplateRepository } from '@src/repositories/VideoTemplateRepository';
import { writeQueueService } from '@src/services/WriteQueueService';
import { createDatoClient, createGQLClient } from '@src/utility/dato';
import { LogManager } from '@src/utility/Logger';
import { getDatocmsReadToken } from '@src/utility/spaCredentials';
import { makeAutoObservable } from 'mobx';

import type { RootStore } from './RootStore';
import type { Client } from '@datocms/cma-client-browser';
import type { AIModel } from '@src/types.ts/ai_prompts';
import type { GraphQLClient } from 'graphql-request';

const logger = LogManager.getLogger('store:DatoClientStore');

export class DatoClientStore {
  private _environment?: string;

  private _datoClient?: Client | ApiClient;
  private _gqlClient?: GraphQLClient;

  // Repositories
  private _repositories: Record<string, any> = {};
  private _baseVideoRepository?: VideoRepository;
  private _cachedVideoRepository?: CachedVideoRepository;
  private _videoRepository?: VideoRepository | CachedVideoRepository;
  private _storyRepository?: StoryRepository;
  private _assetRepository?: AssetRepository;
  private _albumRepository?: AlbumRepository;
  private _transcriptRepository?: TranscriptRepository;
  private _translationRepository?: TranslationRepository;
  private _captionRepository?: CaptionRepository;
  private _commentRepository?: CommentRepository;
  private _audioRepository?: AudioRepository;
  private _speakerRepository?: SpeakerRepository;
  // private _templateRepository?: TemplateRepository;
  private _aiFlowRepository?: AIFlowRepository;
  private _aiModelRepository?: AIModelRepository;
  private _aiPromptRepository?: AIPromptRepository;
  private _templatedPromptsRepository?: TemplatedPromptsRepository;
  private _compositionRepository?: CompositionRepository;
  private _videoTemplateRepository?: VideoTemplateRepository;
  private _releasesRepository?: ReleasesRepository;
  private _distributionChannelRepository?: DistributionChannelRepository;
  private _languageRepository?: LanguageRepository;
  private _personRepository?: PersonRepository;
  private _publishRecordRepository?: PublishRecordRepository;
  private _translationHandlerRepository?: TranslationHandlerRepository;
  private _aiVoiceRepository?: AIVoiceRepository;
  private _dubbingRepository?: DubbingRepository;
  private _translationMemoryCollectionRepository?: TranslationMemoryCollectionRepository;
  private _translationMemoryEntryRepository?: TranslationMemoryEntryRepository;
  private _translationMemoryImportRepository?: TranslationMemoryImportRepository;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this, {
      // gqlAuthToken reads the SPA credential holder — a non-observable
      // module global that throws until populated (PLAT-9905). As a MobX
      // computed it would have no observable dependency to invalidate, so
      // a single read while the holder is empty caches the thrown error
      // for the store's lifetime, permanently breaking the session even
      // after credentials arrive. Keep it a plain getter that re-reads the
      // holder on each access and self-heals once credentials are set.
      gqlAuthToken: false,
    });
  }

  initializeClients() {
    const { userIdentityStore } = this.rootStore;
    const { datoContext, clerkToken, environment } = userIdentityStore;

    this._environment = environment;

    logger.debug('Initializing Dato and GraphQL clients', {
      hasDatoContext: !!datoContext,
      hasClerkToken: !!clerkToken,
      environment,
    });

    // Initialize Dato Client. The plugin must run as an authenticated user — either
    // via DatoCMS context (when embedded) or Clerk (standalone SPA). The previous
    // fallback to a build-time admin token was removed in PLAT-9905; that token was
    // public in the bundle and granted read/write access to every record.
    if (datoContext?.currentUserAccessToken) {
      this._datoClient = createDatoClient({
        apiToken: datoContext.currentUserAccessToken,
        environment: environment,
        logLevel: LogLevel.NONE,
      });
    } else if (clerkToken) {
      this._datoClient = new ApiClient({
        environment: environment,
        authToken: clerkToken,
      });
    } else {
      logger.error('No Dato context token or Clerk token available');
      throw new Error(
        'DatoClientStore: refusing to initialize without authenticated user (no Dato context token and no Clerk token)',
      );
    }
    // Initialize GraphQL Client
    this._gqlClient = createGQLClient({
      includeDrafts: true,
      excludeInvalid: false,
      environment: environment,
      authToken: this.gqlAuthToken,
    });

    // Initialize repositories
    this.initializeRepositories();
  }

  get gqlAuthToken(): string {
    return getDatocmsReadToken();
  }

  private initializeRepositories() {
    if (!this._datoClient || !this._gqlClient) {
      throw new Error('Clients must be initialized before repositories');
    }

    // Create base video repository
    this._baseVideoRepository = new VideoRepository(
      this._datoClient,
      this._gqlClient,
    );

    // Create cached video repository wrapper
    // The cache starts disabled and can be enabled via setCacheEnabled()
    this._cachedVideoRepository = new CachedVideoRepository(
      this._baseVideoRepository,
      writeQueueService,
      { enabled: false },
    );

    // Expose the cached repository as the public interface
    this._videoRepository = this._cachedVideoRepository;

    this._storyRepository = new StoryRepository(
      this._datoClient,
      this._gqlClient,
    );
    this._assetRepository = new AssetRepository(
      this._datoClient,
      this._gqlClient,
    );
    this._albumRepository = new AlbumRepository(
      this._datoClient,
      this._gqlClient,
    );
    this._transcriptRepository = new TranscriptRepository(
      this._datoClient,
      this._gqlClient,
    );
    this._translationRepository = new TranslationRepository(
      this._datoClient,
      this._gqlClient,
    );
    this._captionRepository = new CaptionRepository(this._datoClient);
    this._commentRepository = new CommentRepository(
      this._datoClient,
      this._gqlClient,
    );
    this._audioRepository = new AudioRepository(
      this._datoClient,
      this._assetRepository,
    );
    this._speakerRepository = new SpeakerRepository(
      this._datoClient,
      this._gqlClient,
    );
    // this._templateRepository = new TemplateRepository(
    //   this._datoClient,
    //   this._gqlClient,
    // );
    this._aiFlowRepository = new AIFlowRepository(
      this._datoClient,
      this._gqlClient,
    );
    this._aiModelRepository = new AIModelRepository(this._gqlClient);
    this._aiPromptRepository = new AIPromptRepository(this._gqlClient);
    this._templatedPromptsRepository = new TemplatedPromptsRepository(
      this._datoClient,
      this._gqlClient,
    );
    this._compositionRepository = new CompositionRepository(
      this._datoClient,
      this._gqlClient,
      this._assetRepository,
    );
    this._videoTemplateRepository = new VideoTemplateRepository(
      this._datoClient,
      this._gqlClient,
    );
    this._releasesRepository = new ReleasesRepository(this._gqlClient);
    this._distributionChannelRepository = new DistributionChannelRepository(
      this._gqlClient,
      this._datoClient,
    );
    this._languageRepository = new LanguageRepository(this._gqlClient);
    this._personRepository = new PersonRepository(this._gqlClient);
    this._publishRecordRepository = new PublishRecordRepository(
      this._datoClient,
      this._gqlClient,
    );
    this._translationHandlerRepository = new TranslationHandlerRepository(
      this._gqlClient,
    );
    this._aiVoiceRepository = new AIVoiceRepository(this._gqlClient);
    this._dubbingRepository = new DubbingRepository();
    this._translationMemoryCollectionRepository =
      new TranslationMemoryCollectionRepository(
        this._datoClient,
        this._gqlClient,
      );
    this._translationMemoryEntryRepository =
      new TranslationMemoryEntryRepository(this._datoClient, this._gqlClient);
    this._translationMemoryImportRepository =
      new TranslationMemoryImportRepository(this._datoClient, this._gqlClient);
    // Assign repositories to the _repositories object for programmatic access
    // ie loops, etc
    this._repositories = {
      videoRepository: this._videoRepository,
      storyRepository: this._storyRepository,
      assetRepository: this._assetRepository,
      albumRepository: this._albumRepository,
      transcriptRepository: this._transcriptRepository,
      translationRepository: this._translationRepository,
      captionRepository: this._captionRepository,
      commentRepository: this._commentRepository,
      audioRepository: this._audioRepository,
      speakerRepository: this._speakerRepository,
      // templateRepository: this._templateRepository,
      aiFlowRepository: this._aiFlowRepository,
      aiModelRepository: this._aiModelRepository,
      aiPromptRepository: this._aiPromptRepository,
      templatedPromptsRepository: this._templatedPromptsRepository,
      compositionRepository: this._compositionRepository,
      videoTemplateRepository: this._videoTemplateRepository,
      releasesRepository: this._releasesRepository,
      distributionChannelRepository: this._distributionChannelRepository,
      languageRepository: this._languageRepository,
      publishRecordRepository: this._publishRecordRepository,
      translationHandlerRepository: this._translationHandlerRepository,
      aiVoiceRepository: this._aiVoiceRepository,
      dubbingRepository: this._dubbingRepository,
      translationMemoryCollectionRepository:
        this._translationMemoryCollectionRepository,
      translationMemoryEntryRepository: this._translationMemoryEntryRepository,
      translationMemoryImportRepository:
        this._translationMemoryImportRepository,
    };
  }

  // Client getters
  get datoClient(): Client | ApiClient {
    if (!this._datoClient) {
      throw new Error('Dato client not initialized');
    }
    return this._datoClient;
  }

  get gqlClient(): GraphQLClient {
    if (!this._gqlClient) {
      throw new Error('GraphQL client not initialized');
    }
    return this._gqlClient;
  }

  get environment(): string {
    if (!this._environment) {
      throw new Error('Environment not initialized');
    }
    return this._environment;
  }

  // Repository getters

  /**
   * Returns the video repository (may be cached or base depending on configuration).
   * This is the primary interface for video operations.
   */
  get videoRepository(): VideoRepository | CachedVideoRepository {
    if (!this._videoRepository) {
      throw new Error('Video repository not initialized');
    }
    return this._videoRepository;
  }

  /**
   * Returns the base video repository without caching.
   * Used internally by WriteQueueService to perform actual Dato API calls.
   */
  get baseVideoRepository(): VideoRepository {
    if (!this._baseVideoRepository) {
      throw new Error('Base video repository not initialized');
    }
    return this._baseVideoRepository;
  }

  get hasValidUserAuth(): boolean {
    const { datoContext, clerkToken } = this.rootStore.userIdentityStore;
    return !!(datoContext?.currentUserAccessToken || clerkToken);
  }

  /**
   * Returns the cached video repository if available.
   * Useful for accessing cache-specific methods like getCacheStatus().
   */
  get cachedVideoRepository(): CachedVideoRepository | undefined {
    return this._cachedVideoRepository;
  }

  /**
   * Enable or disable the write-behind cache for video operations.
   * When enabled, saveLightweightVideo() returns immediately with optimistic IDs.
   * @param enabled - Whether to enable caching
   */
  setCacheEnabled(enabled: boolean): void {
    if (this._cachedVideoRepository) {
      this._cachedVideoRepository.setEnabled(enabled);
    }
  }

  get storyRepository(): StoryRepository {
    if (!this._storyRepository) {
      throw new Error('Story repository not initialized');
    }
    return this._storyRepository;
  }

  get assetRepository(): AssetRepository {
    if (!this._assetRepository) {
      throw new Error('Asset repository not initialized');
    }
    return this._assetRepository;
  }

  get albumRepository(): AlbumRepository {
    if (!this._albumRepository) {
      throw new Error('Album repository not initialized');
    }
    return this._albumRepository;
  }

  get transcriptRepository(): TranscriptRepository {
    if (!this._transcriptRepository) {
      throw new Error('Transcript repository not initialized');
    }
    return this._transcriptRepository;
  }

  get translationRepository(): TranslationRepository {
    if (!this._translationRepository) {
      throw new Error('Translation repository not initialized');
    }
    return this._translationRepository;
  }

  get captionRepository(): CaptionRepository {
    if (!this._captionRepository) {
      throw new Error('Caption repository not initialized');
    }
    return this._captionRepository;
  }

  get commentRepository(): CommentRepository {
    if (!this._commentRepository) {
      throw new Error('Comment repository not initialized');
    }
    return this._commentRepository;
  }

  get audioRepository(): AudioRepository {
    if (!this._audioRepository) {
      throw new Error('Audio repository not initialized');
    }
    return this._audioRepository;
  }

  get speakerRepository(): SpeakerRepository {
    if (!this._speakerRepository) {
      throw new Error('Speaker repository not initialized');
    }
    return this._speakerRepository;
  }

  // get templateRepository(): TemplateRepository {
  //   if (!this._templateRepository) {
  //     throw new Error('Template repository not initialized');
  //   }
  //   return this._templateRepository;
  // }

  get aiFlowRepository(): AIFlowRepository {
    if (!this._aiFlowRepository) {
      throw new Error('AI flow repository not initialized');
    }
    return this._aiFlowRepository;
  }

  get aiModelRepository(): AIModelRepository {
    if (!this._aiModelRepository) {
      throw new Error('AI model repository not initialized');
    }
    return this._aiModelRepository;
  }

  async getDefaultAIModel(): Promise<AIModel | undefined> {
    return this.aiModelRepository.getDefaultModel();
  }

  get aiPromptRepository(): AIPromptRepository {
    if (!this._aiPromptRepository) {
      throw new Error('AI prompt repository not initialized');
    }
    return this._aiPromptRepository;
  }

  get templatedPromptsRepository(): TemplatedPromptsRepository {
    if (!this._templatedPromptsRepository) {
      throw new Error('Templated prompts repository not initialized');
    }
    return this._templatedPromptsRepository;
  }

  get compositionRepository(): CompositionRepository {
    if (!this._compositionRepository) {
      throw new Error('Composition repository not initialized');
    }
    return this._compositionRepository;
  }

  get videoTemplateRepository(): VideoTemplateRepository {
    if (!this._videoTemplateRepository) {
      throw new Error('Video template repository not initialized');
    }
    return this._videoTemplateRepository;
  }

  get releasesRepository(): ReleasesRepository {
    if (!this._releasesRepository) {
      throw new Error('Releases repository not initialized');
    }
    return this._releasesRepository;
  }

  get distributionChannelRepository(): DistributionChannelRepository {
    if (!this._distributionChannelRepository) {
      throw new Error('Distribution channel repository not initialized');
    }
    return this._distributionChannelRepository;
  }

  get languageRepository(): LanguageRepository {
    if (!this._languageRepository) {
      throw new Error('Language repository not initialized');
    }
    return this._languageRepository;
  }

  get personRepository(): PersonRepository {
    if (!this._personRepository) {
      throw new Error('Person repository not initialized');
    }
    return this._personRepository;
  }

  get publishRecordRepository(): PublishRecordRepository {
    if (!this._publishRecordRepository) {
      throw new Error('Publish record repository not initialized');
    }
    return this._publishRecordRepository;
  }

  get translationHandlerRepository(): TranslationHandlerRepository {
    if (!this._translationHandlerRepository) {
      throw new Error('Translation handler repository not initialized');
    }
    return this._translationHandlerRepository;
  }

  get aiVoiceRepository(): AIVoiceRepository {
    if (!this._aiVoiceRepository) {
      throw new Error('AI voice repository not initialized');
    }
    return this._aiVoiceRepository;
  }

  get dubbingRepository(): DubbingRepository {
    if (!this._dubbingRepository) {
      throw new Error('Dubbing repository not initialized');
    }
    return this._dubbingRepository;
  }

  get translationMemoryCollectionRepository(): TranslationMemoryCollectionRepository {
    if (!this._translationMemoryCollectionRepository) {
      throw new Error(
        'Translation memory collection repository not initialized',
      );
    }
    return this._translationMemoryCollectionRepository;
  }

  get translationMemoryEntryRepository(): TranslationMemoryEntryRepository {
    if (!this._translationMemoryEntryRepository) {
      throw new Error('Translation memory entry repository not initialized');
    }
    return this._translationMemoryEntryRepository;
  }

  get translationMemoryImportRepository(): TranslationMemoryImportRepository {
    if (!this._translationMemoryImportRepository) {
      throw new Error('Translation memory import repository not initialized');
    }
    return this._translationMemoryImportRepository;
  }

  get isReady(): boolean {
    const reposReady = Object.values(this._repositories).every(repo => !!repo);

    return !!(this._datoClient && this._gqlClient && reposReady);
  }
}
